import { Navigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL, fmtNum, fmtDur } from "@/lib/format";
import {
  AlertTriangle, CheckCircle2, Fingerprint, Hash, Search, ShieldAlert, ShieldCheck, Sigma, Layers, Clock, ArrowRight, Zap, Target
} from "lucide-react";
import type { AuditCategory, AuditSeverity } from "@/lib/fiscal-types";

interface RuleSpec {
  code: string; layer: 1 | 2 | 3 | 4; category: AuditCategory; title: string; desc: string; severity: AuditSeverity; status: "ATIVA" | "PENDENTE_BACKEND";
}

const RULES: RuleSpec[] = [
  { code: "R1",  layer: 1, category: "INTEGRIDADE", title: "Hash SHA-256 do XML original", desc: "Cada XML recebe um hash SHA-256 imutável. Duplicatas exatas (byte a byte) são ignoradas automaticamente.", severity: "INFO",   status: "ATIVA" },
  { code: "R4",  layer: 2, category: "MATEMATICA",  title: "vProd = qCom × vUnCom", desc: "Para cada item, valida que o produto da quantidade pela unitário bate com vProd (aritmética em centavos).", severity: "MEDIA",   status: "ATIVA" },
  { code: "R5",  layer: 2, category: "MATEMATICA",  title: "Totais ICMSTot vs soma dos itens", desc: "vProd, vICMS, vST, vPIS, vCOFINS, vIPI declarados batem com a soma item-a-item, com tolerância dinâmica.", severity: "MEDIA",   status: "ATIVA" },
  { code: "R6",  layer: 2, category: "MATEMATICA",  title: "Fórmula oficial do vNF", desc: "vNF = vProd + vFrete + vSeg + vOutro + vII + vIPI + vST + vFCPST − vDesc − vICMSDeson.", severity: "ALTA",    status: "ATIVA" },
  { code: "R7",  layer: 3, category: "TRIBUTARIA",  title: "CST × valores ICMS", desc: "CST 00/10/20/70/90 exigem vBC, pICMS, vICMS > 0. CST 40/41/50/51 exigem vICMS = 0.", severity: "ALTA",    status: "ATIVA" },
  { code: "R8",  layer: 3, category: "TRIBUTARIA",  title: "CFOP × tipo de operação (tpNF)", desc: "CFOPs 1/2/3 são entrada (tpNF=0); 5/6/7 são saída (tpNF=1).", severity: "MEDIA",   status: "ATIVA" },
  { code: "R9",  layer: 3, category: "PROTOCOLO",   title: "Ambiente vs protocolo", desc: "tpAmb=1 (produção) exige nProt e dhRecbto. tpAmb=2 = HOMOLOGAÇÃO (sem valor fiscal).", severity: "CRITICA", status: "ATIVA" },
  { code: "R10", layer: 3, category: "INTEGRIDADE", title: "Duplicidade por chave de acesso", desc: "Detecta XMLs distintos com a mesma chave de 44 dígitos (re-emissão suspeita).", severity: "ALTA",    status: "ATIVA" },
  { code: "R11", layer: 4, category: "COERENCIA",   title: "Valor alto com imposto zero", desc: "vNF > R$ 10.000 com ICMS = 0 e sem CST de isenção é sinalizado para verificação técnica.", severity: "MEDIA",   status: "ATIVA" },
  { code: "R13", layer: 4, category: "COERENCIA",   title: "Coerência de transporte", desc: "modFrete ≠ 9 (sem transporte) exige preenchimento do grupo <transporta>.", severity: "MEDIA",   status: "ATIVA" },
  { code: "R14", layer: 4, category: "COERENCIA",   title: "Soma de pagamentos vs vNF", desc: "Σ vPag − vTroco = vNF. Detecta divergências entre <pag> e <total>.", severity: "ALTA",    status: "ATIVA" },
  { code: "R15", layer: 4, category: "TRIBUTARIA",  title: "IBS/CBS (Reforma Tributária)", desc: "vIBS = vIBSUF + vIBSMun e vCBS calculados conforme schema da reforma.", severity: "ALTA",    status: "ATIVA" },
];

const SEV_STYLES: Record<AuditSeverity, { bg: string; text: string; border: string; label: string }> = {
  CRITICA: { bg: "bg-red-500/10", text: "text-red-500", border: "border-red-500/20", label: "CRÍTICA" },
  ALTA:    { bg: "bg-orange-500/10", text: "text-orange-500", border: "border-orange-500/20", label: "ALTA" },
  MEDIA:   { bg: "bg-amber-500/10", text: "text-amber-500", border: "border-amber-500/20", label: "MÉDIA" },
  BAIXA:   { bg: "bg-yellow-500/10", text: "text-yellow-500", border: "border-yellow-500/20", label: "BAIXA" },
  INFO:    { bg: "bg-sky-500/10", text: "text-sky-500", border: "border-sky-500/20", label: "INFO" },
};

function SeverityBadge({ s }: { s: AuditSeverity }) {
  const sty = SEV_STYLES[s];
  return (
    <span className={`px-2 py-0.5 rounded text-[9px] font-black tracking-wider border uppercase ${sty.bg} ${sty.text} ${sty.border}`}>
      {sty.label}
    </span>
  );
}

export default function InspecaoPage() {
  const { dashboard, duracaoMs, notas } = useFiscalStore();
  const [filtroRegra, setFiltroRegra] = useState<string | null>(null);
  const [busca, setBusca] = useState("");

  useEffect(() => { document.title = "Inspeção de Malha — Extrator Fiscal"; }, []);

  const audit = dashboard?.auditSummary;
  const notasComFindings = useMemo(() => notas.filter((n) => (n.auditFindings?.length ?? 0) > 0), [notas]);
  const notasFiltradas = useMemo(() => {
    let arr = notasComFindings;
    if (filtroRegra) arr = arr.filter((n) => n.auditFindings?.some((f) => f.regra === filtroRegra));
    if (busca.trim()) {
      const q = busca.trim().toLowerCase();
      arr = arr.filter((n) => n.chave.toLowerCase().includes(q) || n.numero.toLowerCase().includes(q) || n.emitente.toLowerCase().includes(q));
    }
    return arr.slice(0, 100);
  }, [notasComFindings, filtroRegra, busca]);

  if (!dashboard) return <Navigate to="/upload" replace />;

  return (
    <div className="px-8 py-8 max-w-[1600px] mx-auto space-y-8 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight dark:text-white">Inspeção de Malha Fiscal</h1>
          <p className="text-sm text-muted-foreground mt-1">Inspeção profunda de {fmtNum(notas.length)} XMLs em 4 camadas de validação arquitetural.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-sky-500/10 border border-sky-500/20 rounded-lg text-sky-600 dark:text-sky-500 text-[10px] font-bold uppercase tracking-widest glass">
            <Fingerprint className="size-3" /> SHA-256 ATIVO
          </div>
        </div>
      </header>

      {/* KPIs Uniformizados com Glass */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Notas Validadas" value={fmtNum(notas.length)} sub="Integridade 100%" icon={CheckCircle2} color="text-emerald-500" />
        <KpiCard label="Indícios Detectados" value={fmtNum(audit?.totalFindings ?? 0)} sub={`Em ${fmtNum(audit?.notasComAchados ?? 0)} notas`} icon={ShieldAlert} color="text-orange-500" />
        <KpiCard label="Duplicatas" value={fmtNum(audit?.duplicatasIgnoradas ?? 0)} sub="Ignoradas por Hash" icon={Hash} color="text-sky-500" />
        <KpiCard label="Volume Inspecionado" value={fmtBRL(dashboard.stats.totalRevenue)} sub="Receita Analisada" icon={Sigma} color="text-primary" />
      </div>

      {/* Severidade Glass */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-bold mb-6 flex items-center gap-2"><Layers className="size-5 text-primary" /> Gravidade dos Achados</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {(["CRITICA", "ALTA", "MEDIA", "BAIXA", "INFO"] as AuditSeverity[]).map((s) => {
            const sty = SEV_STYLES[s];
            const n = audit?.porSeveridade[s] ?? 0;
            return (
              <div key={s} className={`p-4 rounded-xl border flex flex-col justify-between transition-transform hover:-translate-y-1 ${sty.bg} ${sty.border} glass`}>
                <span className={`text-[10px] font-black tracking-widest ${sty.text}`}>{sty.label}</span>
                <span className="text-2xl font-black tabular-nums mt-1 dark:text-white">{fmtNum(n)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Regras e Resultados */}
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="glass-card p-6 lg:col-span-1 flex flex-col h-[740px]">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold flex items-center gap-2"><Target className="size-5 text-emerald-500" /> Matriz de Regras Ativas</h2>
            {filtroRegra && <button onClick={() => setFiltroRegra(null)} className="text-[10px] font-bold text-primary uppercase bg-primary/10 px-2 py-1 rounded">Limpar</button>}
          </div>
          <div className="space-y-2 flex-1 overflow-auto pr-2 custom-scrollbar">
            {RULES.map((r) => {
              const count = audit?.porRegra?.[r.code] ?? 0;
              const selected = filtroRegra === r.code;
              return (
                <div key={r.code} onClick={() => count > 0 && setFiltroRegra(selected ? null : r.code)}
                  className={`p-3 rounded-xl border transition-all duration-300 ${count > 0 ? 'cursor-pointer hover:shadow-md' : 'cursor-not-allowed opacity-60'} ${selected ? "bg-primary/10 border-primary/40 ring-1 ring-primary/20" : "bg-slate-100/50 border-slate-200 dark:bg-white/5 dark:border-white/10 dark:hover:bg-white/10 hover:bg-slate-200/50"}`}>
                  <div className="flex justify-between items-start mb-1">
                    <span className="text-[10px] font-black text-primary uppercase bg-primary/10 px-1.5 py-0.5 rounded">{r.code} · Camada {r.layer}</span>
                    <span className={`text-[10px] font-black ${count > 0 ? "text-orange-500" : "text-emerald-500"}`}>{count > 0 ? fmtNum(count) : "✓ OK"}</span>
                  </div>
                  <div className="text-xs font-bold text-slate-800 dark:text-white mb-1">{r.title}</div>
                  <p className="text-[10px] text-muted-foreground leading-tight">{r.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-2 glass-card p-6 flex flex-col h-[740px]">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <h2 className="text-lg font-bold flex items-center gap-2"><AlertTriangle className="size-5 text-orange-500" /> Histórico de Incidentes</h2>
            <div className="relative flex-1 max-w-sm">
              <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input 
                value={busca} 
                onChange={(e) => setBusca(e.target.value)} 
                placeholder="Filtrar por chave de acesso ou número..." 
                className="w-full pl-10 pr-4 py-2 bg-transparent border border-slate-300 dark:border-white/20 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/50 transition-all dark:text-white glass" 
              />
            </div>
          </div>
          <div className="space-y-3 flex-1 overflow-auto pr-2 custom-scrollbar">
            {notasFiltradas.map((n) => (
              <NotaAuditRow key={n.chave} nota={n} regraFiltro={filtroRegra} />
            ))}
            {notasFiltradas.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground opacity-60">
                <ShieldCheck className="size-12 mb-3" />
                <span className="text-sm font-medium">Nenhum achado corresponde aos filtros atuais.</span>
              </div>
            )}
          </div>
          
          <div className="mt-6 pt-4 border-t border-slate-200 dark:border-white/10 grid md:grid-cols-2 gap-4 shrink-0">
            <div className="flex items-center gap-4 p-3 rounded-xl bg-slate-50 border border-slate-200 dark:bg-white/5 dark:border-white/10 glass">
              <div className="p-2 rounded-lg bg-primary/10 text-primary"><Clock className="size-4" /></div>
              <div>
                <div className="text-[9px] font-black uppercase text-muted-foreground">Tempo Processamento</div>
                <div className="text-sm font-black dark:text-white tabular-nums">{fmtDur(duracaoMs)}</div>
              </div>
            </div>
            <div className="flex items-center gap-4 p-3 rounded-xl bg-slate-50 border border-slate-200 dark:bg-white/5 dark:border-white/10 glass">
              <div className="p-2 rounded-lg bg-sky-500/10 text-sky-600 dark:text-sky-500"><Zap className="size-4" /></div>
              <div>
                <div className="text-[9px] font-black uppercase text-muted-foreground">Vazão do Motor (Throughput)</div>
                <div className="text-sm font-black dark:text-white tabular-nums">{(notas.length / Math.max(1, duracaoMs / 1000)).toFixed(0)} notas/s</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NotaAuditRow({ nota, regraFiltro }: any) {
  const [open, setOpen] = useState(false);
  const findings = nota.auditFindings ?? [];
  const visiveis = regraFiltro ? findings.filter((f: any) => f.regra === regraFiltro) : findings;
  const sevMax = nota.auditSeveridadeMax || "INFO";

  return (
    <div className="rounded-xl border border-slate-200 dark:border-white/10 bg-white/50 dark:bg-slate-900/30 overflow-hidden transition-all hover:border-primary/30 shadow-sm glass">
      <button onClick={() => setOpen(!open)} className="w-full text-left p-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <SeverityBadge s={sevMax} />
          <div className="min-w-0">
            <div className="text-sm font-bold text-slate-800 dark:text-white truncate">NF {nota.numero}/{nota.serie} — {nota.emitente}</div>
            <div className="text-[10px] font-mono text-muted-foreground truncate opacity-80">{nota.chave}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black text-primary bg-primary/10 px-2 py-1 rounded-md">{visiveis.length} ACHADOS</span>
          <ArrowRight className={`size-4 text-primary transition-transform ${open ? "rotate-90" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 animate-in slide-in-from-top-2">
          {visiveis.map((f: any, i: number) => (
            <div key={i} className="flex gap-3 p-3 rounded-lg bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 glass">
              <div className="shrink-0 pt-0.5"><SeverityBadge s={f.severidade} /></div>
              <div className="text-xs leading-relaxed">
                <span className="font-black text-primary mr-2 bg-primary/10 px-1.5 py-0.5 rounded text-[10px]">{f.regra}</span>
                <span className="text-slate-700 dark:text-slate-300 font-medium">{f.mensagem}</span>
                {f.campo && <div className="text-[10px] font-mono text-muted-foreground mt-1.5 opacity-80 border-t border-slate-200 dark:border-white/10 pt-1">TAG XML: <span className="text-primary">{f.campo}</span></div>}
              </div>
            </div>
          ))}
          <div className="pt-3 mt-3 flex items-center justify-between text-[10px] font-mono text-muted-foreground border-t border-slate-200 dark:border-white/10">
            <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded"><Fingerprint className="size-3 text-sky-500" /> {nota.xmlHash}</div>
            <div className="bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded font-bold">{fmtNum(nota.xmlBytes || 0)} BYTES</div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, color }: { label: string; value: string; sub: string; icon: any; color: string }) {
  return (
    <div className="glass-card p-5 hover-lift group">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg group-hover:bg-primary group-hover:text-white transition-colors">
          <Icon className="size-4" />
        </div>
        <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">{label}</span>
      </div>
      <div className={`text-2xl font-black tabular-nums dark:text-white ${color}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground mt-1">{sub}</div>
    </div>
  );
}
